import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  CreateProductPictureImportParams,
  ProductPictureImportsFilterParams,
  ProductPictureImportsListResponse,
  ProductPictureImportType
} from '@/types/product-picture-import'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'

// API functions
import {
  bulkDeleteProductPictureImports,
  createProductPictureImport,
  deleteProductPictureImport,
  fetchProductPictureImport,
  fetchProductPictureImports,
  retryProductPictureImport
} from '@/lib/api'

type ProductPictureImportStateType = {
  data: ProductPictureImportType | null
  isLoading: boolean
  error: string | null
}

type ProductPictureImportsListStateType = {
  data: ProductPictureImportsListResponse | null
  isLoading: boolean
  error: string | null
}

type ProductPictureImportsStoreState = {
  productPictureImport: ProductPictureImportStateType
  productPictureImportsList: ProductPictureImportsListStateType
  filters: ProductPictureImportsFilterParams
  selectedProductPictureImportIds: string[]
  setFilters: (filters: Partial<ProductPictureImportsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchProductPictureImports: (params?: ProductPictureImportsFilterParams) => Promise<ProductPictureImportsListResponse>
  resetProductPictureImportsList: () => void

  // ProductPictureImport operations
  getProductPictureImport: (id: string) => Promise<ProductPictureImportType>
  createProductPictureImport: (params: CreateProductPictureImportParams) => Promise<ProductPictureImportType>
  deleteProductPictureImport: (id: string) => Promise<void>
  retryProductPictureImport: (id: string) => Promise<ProductPictureImportType>
  bulkDeleteProductPictureImports: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectProductPictureImport: (id: string) => void
  deselectProductPictureImport: (id: string) => void
  toggleProductPictureImportSelection: (id: string) => void
  selectAllProductPictureImports: (productPictureImports: ProductPictureImportType[]) => void
  deselectAllProductPictureImports: () => void
  isProductPictureImportSelected: (id: string) => boolean
  resetProductPictureImport: () => void
}

const defaultFilters: ProductPictureImportsFilterParams = {
  statuses: undefined,
  sort: SortEnum.ASC,
  sortBy: 'translations.en-US.title',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const useProductPictureImportsStore = create<ProductPictureImportsStoreState>()(
  devtools((set, get) => ({
    productPictureImport: {
      data: null,
      isLoading: false,
      error: null
    },
    productPictureImportsList: {
      data: null,
      isLoading: false,
      error: null
    },
    selectedProductPictureImportIds: [],

    filters: defaultFilters,

    setFilters: filters => {
      set(state => ({
        filters: { ...state.filters, ...filters }
      }))
    },

    resetFilters: () => {
      set({ filters: defaultFilters })
    },

    updateUrlParams: searchParams => {
      const filters: Partial<ProductPictureImportsFilterParams> = {}

      for (const [key, value] of searchParams.entries()) {
        if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as ProductPictureImportsFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers' || key === 'statuses') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof ProductPictureImportsFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (
            (key === 'createdUsers' || key === 'updatedUsers' || key === 'categories' || key === 'statuses') &&
            Array.isArray(value)
          ) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            }
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getProductPictureImport: async id => {
      set(state => ({
        productPictureImport: {
          ...state.productPictureImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchProductPictureImport(id)

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createProductPictureImport: async params => {
      set(state => ({
        productPictureImport: {
          ...state.productPictureImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createProductPictureImport(params)

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    retryProductPictureImport: async id => {
      set(state => ({
        productPictureImport: {
          ...state.productPictureImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await retryProductPictureImport(id)

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteProductPictureImport: async id => {
      set(state => ({
        productPictureImport: {
          ...state.productPictureImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteProductPictureImport(id)

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetProductPictureImport: () => {
      set(state => ({
        productPictureImport: {
          ...state.productPictureImport,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectProductPictureImport: id => {
      set(state => {
        if (state.selectedProductPictureImportIds.includes(id)) {
          return state
        }
        return { selectedProductPictureImportIds: [...state.selectedProductPictureImportIds, id] }
      })
    },

    deselectProductPictureImport: id => {
      set(state => ({
        selectedProductPictureImportIds: state.selectedProductPictureImportIds.filter(
          productPictureImportId => productPictureImportId !== id
        )
      }))
    },

    toggleProductPictureImportSelection: id => {
      set(state => {
        const isSelected = state.selectedProductPictureImportIds.includes(id)
        if (isSelected) {
          return {
            selectedProductPictureImportIds: state.selectedProductPictureImportIds.filter(
              productPictureImportId => productPictureImportId !== id
            )
          }
        } else {
          return { selectedProductPictureImportIds: [...state.selectedProductPictureImportIds, id] }
        }
      })
    },

    selectAllProductPictureImports: productPictureImports => {
      set({
        selectedProductPictureImportIds: productPictureImports.map(productPictureImport => productPictureImport.id)
      })
    },

    deselectAllProductPictureImports: () => {
      set({ selectedProductPictureImportIds: [] })
    },

    isProductPictureImportSelected: id => {
      return get().selectedProductPictureImportIds.includes(id)
    },

    bulkDeleteProductPictureImports: async ids => {
      set(state => ({
        productPictureImport: {
          ...state.productPictureImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteProductPictureImports({ ids })

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedProductPictureImportIds: state.selectedProductPictureImportIds.filter(
            id => !response.deletedItems.includes(id)
          )
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productPictureImport: {
            ...state.productPictureImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchProductPictureImports: async (params?: ProductPictureImportsFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        productPictureImportsList: {
          ...state.productPictureImportsList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchProductPictureImports(filtersToUse)

        set(state => ({
          productPictureImportsList: {
            ...state.productPictureImportsList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productPictureImportsList: {
            ...state.productPictureImportsList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetProductPictureImportsList: () => {
      set(state => ({
        productPictureImportsList: {
          ...state.productPictureImportsList,
          data: null,
          error: null
        }
      }))
    }
  }))
)

export default useProductPictureImportsStore
